import React, { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, Inbox } from "lucide-react";

const EMPTY_IMG = "/empty-illustration.png";

export default function Withdrawals() {
    const { t } = useI18n();
    const ctx = useOutletContext() || {};
    const stats = ctx.stats || { new: 0, in_progress: 0, done: 0, total: 0 };
    const [items, setItems] = useState([]);
    const [shops, setShops] = useState([]);
    const [loading, setLoading] = useState(true);
    const [shopFilter, setShopFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (shopFilter !== "all") params.shop_id = shopFilter;
            if (statusFilter !== "all") params.status = statusFilter;
            const [{ data: list }, { data: sl }] = await Promise.all([
                api.get("/withdrawals", { params }),
                api.get("/shops"),
            ]);
            setItems(list);
            setShops(sl);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [shopFilter, statusFilter]);

    const shopMap = useMemo(() => Object.fromEntries(shops.map((s) => [s.id, s])), [shops]);

    const downloadCsv = async () => {
        const token = localStorage.getItem("wr_token");
        const url = `${API_BASE}/withdrawals/export/csv${shopFilter !== "all" ? `?shop_id=${shopFilter}` : ""}`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        const blob = await res.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "widerrufe.csv";
        a.click();
    };

    const fmtDate = (iso) => {
        try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso; }
    };

    return (
        <div data-testid="withdrawals-page">
            <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight text-[#00153f]">{t("wd_title")}</h1>
                    <p className="text-gray-600 mt-1">{t("wd_sub")}</p>
                </div>
                <Button onClick={downloadCsv} data-testid="btn-export-csv" variant="outline" className="border-slate-300">
                    <Download className="w-4 h-4 mr-2" /> {t("wd_export_csv")}
                </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                {[
                    { k: "total", v: stats.total, label: t("dash_stat_total") },
                    { k: "new", v: stats.new, label: t("status_new") },
                    { k: "in_progress", v: stats.in_progress, label: t("status_in_progress") },
                    { k: "accepted", v: stats.accepted, label: t("status_accepted") },
                    { k: "rejected", v: stats.rejected, label: t("status_rejected") },
                ].map((s) => (
                    <div key={s.k} className="border border-gray-200 rounded-md bg-white p-5">
                        <div className="text-xs text-gray-500 font-mono uppercase tracking-widest">{s.label}</div>
                        <div className="font-display text-3xl font-bold text-[#00153f] mt-2">{s.v}</div>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div className="flex gap-3 mb-4 flex-wrap">
                <Select value={shopFilter} onValueChange={setShopFilter}>
                    <SelectTrigger data-testid="filter-shop" className="w-56"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t("wd_filter_all")}</SelectItem>
                        {shops.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger data-testid="filter-status" className="w-56"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t("wd_filter_status_all")}</SelectItem>
                        <SelectItem value="new">{t("status_new")}</SelectItem>
                        <SelectItem value="in_progress">{t("status_in_progress")}</SelectItem>
                        <SelectItem value="accepted">{t("status_accepted")}</SelectItem>
                        <SelectItem value="rejected">{t("status_rejected")}</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="border border-gray-200 rounded-md bg-white overflow-hidden">
                {loading ? (
                    <div className="p-8 text-sm text-gray-500">{t("loading")}</div>
                ) : items.length === 0 ? (
                    <div className="p-12 text-center" data-testid="withdrawals-empty">
                        <img src={EMPTY_IMG} alt="" className="w-32 h-32 mx-auto object-contain mb-4 opacity-90" />
                        <div className="font-display text-xl font-semibold text-[#00153f]">{t("wd_empty_title")}</div>
                        <p className="text-gray-500 mt-2 max-w-sm mx-auto">{t("wd_empty_sub")}</p>
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-slate-50">
                                <TableHead className="font-mono text-xs uppercase tracking-widest text-gray-600">{t("wd_col_ref")}</TableHead>
                                <TableHead className="font-mono text-xs uppercase tracking-widest text-gray-600">{t("wd_col_date")}</TableHead>
                                <TableHead className="font-mono text-xs uppercase tracking-widest text-gray-600">{t("wd_col_customer")}</TableHead>
                                <TableHead className="font-mono text-xs uppercase tracking-widest text-gray-600">{t("wd_col_order")}</TableHead>
                                <TableHead className="font-mono text-xs uppercase tracking-widest text-gray-600">{t("wd_col_shop")}</TableHead>
                                <TableHead className="font-mono text-xs uppercase tracking-widest text-gray-600">{t("wd_col_status")}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {items.map((w) => (
                                <TableRow key={w.id} className="hover:bg-slate-50 cursor-pointer" data-testid={`wd-row-${w.id}`}>
                                    <TableCell className="font-mono text-xs">
                                        <Link to={`/app/withdrawals/${w.id}`} className="text-[#00153f] hover:underline">
                                            {w.id.slice(0, 8).toUpperCase()}
                                        </Link>
                                    </TableCell>
                                    <TableCell className="text-sm text-gray-600">{fmtDate(w.created_at)}</TableCell>
                                    <TableCell className="text-sm">
                                        <div className="font-medium text-[#00153f]">{w.full_name}</div>
                                        <div className="text-xs text-gray-500">{w.email}</div>
                                    </TableCell>
                                    <TableCell className="text-sm">{w.order_number}</TableCell>
                                    <TableCell className="text-sm">{shopMap[w.shop_id]?.name || "—"}</TableCell>
                                    <TableCell>
                                        <span className={`status-pill status-${w.status}`}>
                                            <span className="dot" />
                                            {t("status_" + w.status)}
                                        </span>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </div>
        </div>
    );
}
